import type { ConfirmationRequest } from '@/hooks/use-agent-chat.hook';

/** Clu's one LinkedIn message or invitation, confirmed on its card. */
export const SEND_LINKEDIN_ACTION = 'send_linkedin_action';

const ACTION_LABEL: Record<string, string> = {
  message: 'Message',
  invite: 'Invite',
};

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * The LinkedIn action a send card asks for, from the card's own arguments
 * (what the server stored and sends on Confirm): From, To, Action, how many
 * the account has left today when the server says, then the message or the
 * invitation's note as plain text. Null for any other card.
 */
export function SendLinkedinPreview({
  request,
}: {
  request: ConfirmationRequest;
}) {
  if (request.tool_name !== SEND_LINKEDIN_ACTION) return null;
  const args = request.arguments ?? {};
  const rows: [string, string][] = [
    ['From', text(args.account_name)],
    ['To', text(args.recipient) || text(args.linkedin_url)],
    ['Action', ACTION_LABEL[text(args.action)] ?? ''],
  ];
  const left = (request as { details?: { left_today?: unknown } }).details
    ?.left_today;
  if (typeof left === 'number' && Number.isFinite(left)) {
    rows.push(['Left today', String(left)]);
  }
  const body = text(args.text).trim();
  return (
    <div
      className='overflow-hidden rounded-[8px] border border-border bg-background text-[12px] leading-[16px]'
      data-testid='send-linkedin-preview'
    >
      <dl className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-[12px] gap-y-[4px] px-[10px] py-[8px]'>
        {rows.map(([label, value]) => (
          <div key={label} className='contents'>
            <dt className='text-muted-foreground'>{label}</dt>
            <dd className='min-w-0 font-medium break-words text-foreground'>
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {body ? (
        <p className='max-h-[240px] overflow-y-auto border-t border-border px-[10px] py-[8px] break-words whitespace-pre-wrap text-foreground'>
          {body}
        </p>
      ) : null}
    </div>
  );
}
